import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import VknLookupField from "@/components/VknLookupField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Loader2, Truck, Star, Search, ArrowRight, MapPin, Phone, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import SupplierMaterialPicker from "@/components/SupplierMaterialPicker";
import DefinitionPicker from "@/components/DefinitionPicker";
import { PROVINCES, districtsOf } from "@/lib/turkey_provinces";
import useViewMode from "@/hooks/useViewMode";
import ViewToggle from "@/components/ViewToggle";
import { useAuth } from "@/context/AuthContext";
import ExcelPartyImportDialog from "@/components/ExcelPartyImportDialog";

// Türkçe locale-safe küçük harf dönüşümü — "İ" ve "I" karakterlerini
// doğru şekilde işler.

const empty = {
  name: "",
  contact_person: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  district: "",
  tax_no: "",
  tax_office: "",
  material_tags: [],
  brand_names: [],
  category_names: [],
  notes: "",
  fabric_ids: [],
  accessory_ids: [],
};

export default function Suppliers() {
  const { can } = useAuth();
  const canExcelImport = can("suppliers", "add");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useViewMode("suppliers", "list");
  const navigate = useNavigate();

  // Arama server-side (Ürün Markaları + bağlı kumaş/aksesuar dahil)
  const filtered = items;

  const load = async (q = search) => {
    setLoading(true);
    try {
      const { data } = await api.get("/suppliers", { params: q.trim() ? { search: q.trim() } : {} });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => load(search), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/suppliers", form);
      toast.success("Tedarikçi eklendi");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e, id) => {
    e?.stopPropagation();
    if (!window.confirm("Bu tedarikçi ve bağlı etiketler kaldırılacak. Devam?")) return;
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success("Tedarikçi silindi");
      setItems((prev) => prev.filter((s) => s.id !== id));
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allChecked = filtered.length > 0 && filtered.every((s) => selected.has(s.id));
  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allChecked) filtered.forEach((s) => n.delete(s.id));
    else filtered.forEach((s) => n.add(s.id));
    return n;
  });

  const removeSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} tedarikçi silinecek. Devam edilsin mi?`)) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    const okIds = [];
    for (const id of ids) {
      try {
        await api.delete(`/suppliers/${id}`);
        ok++; okIds.push(id);
      } catch { fail++; }
    }
    setItems((prev) => prev.filter((s) => !okIds.includes(s.id)));
    setSelected(new Set());
    setBulkBusy(false);
    if (fail === 0) toast.success(`${ok} tedarikçi silindi`);
    else toast.warning(`${ok} tedarikçi silindi, ${fail} tanesi silinemedi`);
  };

  return (
    <div>
      <PageHeader
        title="Tedarikçi Yönetimi"
        subtitle="Aynı malzeme birden çok tedarikçiden — çoklu tedarikçi haritası"
        breadcrumbs={["Modüller", "Tedarikçiler"]}
        actions={
          <div className="flex items-center gap-2">
            {canExcelImport && (
              <Button
                data-testid="excel-import-open-btn"
                onClick={() => setExcelImportOpen(true)}
                variant="outline"
                className="rounded-sm uppercase tracking-wider text-xs font-semibold border-slate-700 hover:border-blue-600 hover:text-blue-400 transition-colors"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel'den Tedarikçi Aktar
              </Button>
            )}
            <Button
              data-testid="add-supplier-btn"
              onClick={() => setOpen(true)}
              className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" /> Yeni Tedarikçi
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              data-testid="supplier-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ara: firma, yetkili, telefon, kategori, malzeme…"
              className="bg-slate-900 border-slate-700 rounded-sm focus-visible:ring-blue-500 pl-10 h-10"
            />
          </div>
          {selected.size > 0 && (
            <Button
              data-testid="bulk-delete-suppliers-btn"
              onClick={removeSelected}
              disabled={bulkBusy}
              variant="outline"
              className="rounded-sm border-rose-800 text-rose-300 hover:bg-rose-950/30 uppercase tracking-wider text-xs font-semibold"
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Seçilenleri Sil ({selected.size})
            </Button>
          )}
          <div className="text-[11px] font-mono text-slate-500 ml-auto">
            {loading ? "" : `${filtered.length} tedarikçi`}
          </div>
          <ViewToggle value={view} onChange={setView} testId="supplier-view" />
        </div>
        <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 px-6">
              <Truck className="w-10 h-10 mx-auto text-slate-700 mb-3" />
              <div className="text-slate-400 text-sm">
                {search ? "Aramaya uygun tedarikçi bulunamadı." : "Henüz tedarikçi yok."}
              </div>
            </div>
          ) : view === "grid" ? (
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="supplier-cards">
              {filtered.map((s) => (
                <div
                  key={s.id}
                  data-testid={`supplier-card-${s.id}`}
                  onClick={() => navigate(`/tedarikci/${s.id}`)}
                  className={`group relative rounded-sm border border-slate-800 bg-slate-950/40 p-3 cursor-pointer transition-colors hover:bg-slate-900/60 hover:border-slate-700 ${selected.has(s.id) ? "border-blue-700 bg-blue-950/20" : ""}`}
                >
                  <input
                    type="checkbox"
                    data-testid={`supplier-card-select-${s.id}`}
                    checked={selected.has(s.id)}
                    onChange={() => toggleSelect(s.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-2 right-2 z-10"
                  />
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-slate-100 truncate">{s.name}</div>
                      {s.contact_person && (
                        <div className="text-[11px] text-slate-400 truncate">{s.contact_person}</div>
                      )}
                    </div>
                    {typeof s.rating === "number" && s.rating > 0 && (
                      <div className="flex items-center gap-0.5 text-amber-400 text-[11px] font-mono">
                        <Star className="w-3 h-3 fill-current" /> {s.rating}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1 text-[11px]">
                    {s.phone && (
                      <div className="flex items-center gap-1.5 text-slate-400 font-mono">
                        <Phone className="w-3 h-3" /> {s.phone}
                      </div>
                    )}
                    {(s.city || s.district) && (
                      <div className="flex items-center gap-1.5 text-slate-400 font-mono">
                        <MapPin className="w-3 h-3" /> {s.city}{s.district && ` · ${s.district}`}
                      </div>
                    )}
                    {s.tax_no && <div className="text-slate-500 font-mono">VKN: {s.tax_no}</div>}
                  </div>
                  {((s.brand_names || []).length > 0 || (s.category_names || []).length > 0) && (
                    <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-800">
                      {(s.brand_names || []).slice(0, 2).map((t, i) => (
                        <span key={`b-${i}`} className="text-[9px] px-1.5 py-0.5 border border-emerald-900/60 text-emerald-300 bg-emerald-950/30 rounded-sm font-mono">
                          {t}
                        </span>
                      ))}
                      {(s.brand_names || []).length > 2 && (
                        <span className="text-[9px] px-1 py-0.5 text-emerald-400 font-mono">+{s.brand_names.length - 2}</span>
                      )}
                      {(s.category_names || []).slice(0, 2).map((t, i) => (
                        <span key={`c-${i}`} className="text-[9px] px-1.5 py-0.5 border border-sky-900/60 text-sky-300 bg-sky-950/30 rounded-sm font-mono">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <ArrowRight className="w-3.5 h-3.5 absolute bottom-2 right-2 text-slate-700 group-hover:text-blue-500 transition-colors" />
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-slate-800">
                  <TableHead className="w-8">
                    <input type="checkbox" data-testid="supplier-select-all" checked={allChecked} onChange={toggleAll} />
                  </TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Yetkili</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">İletişim</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Konum</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Marka / Kategori</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Puan</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} className={`border-slate-800 hover:bg-slate-800/40 cursor-pointer group ${selected.has(s.id) ? "bg-blue-950/20" : ""}`} data-testid={`supplier-row-${s.id}`} onClick={() => navigate(`/tedarikci/${s.id}`)}>
                    <TableCell className="w-8" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" data-testid={`supplier-select-${s.id}`} checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)} />
                    </TableCell>
                    <TableCell className="font-medium text-slate-100">
                      <div>{s.name}</div>
                      {s.tax_no && <div className="text-[10px] font-mono text-slate-500 mt-0.5">V.No {s.tax_no}</div>}
                    </TableCell>
                    <TableCell className="text-slate-300 text-sm">{s.contact_person || "-"}</TableCell>
                    <TableCell className="text-xs">
                      <div className="text-slate-300 font-mono">{s.phone || "-"}</div>
                      <div className="text-slate-500">{s.email || ""}</div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {(s.city || s.district) ? (
                        <div className="text-slate-300 font-mono">
                          {s.city}
                          {s.district && <span className="text-slate-500"> · {s.district}</span>}
                        </div>
                      ) : <span className="text-slate-600">-</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(s.brand_names || []).slice(0, 2).map((t, i) => (
                          <span key={`b-${i}`} data-testid={`supplier-brand-chip-${s.id}-${i}`} className="text-[10px] px-1.5 py-0.5 border border-emerald-900/60 text-emerald-300 bg-emerald-950/30 rounded-sm font-mono">
                            {t}
                          </span>
                        ))}
                        {(s.brand_names || []).length > 2 && (
                          <span className="text-[10px] px-1 py-0.5 text-emerald-400 font-mono" data-testid={`supplier-brand-more-${s.id}`}>+{s.brand_names.length - 2}</span>
                        )}
                        {(s.category_names || []).slice(0, 3).map((t, i) => (
                          <span key={`c-${i}`} className="text-[10px] px-1.5 py-0.5 border border-sky-900/60 text-sky-300 bg-sky-950/30 rounded-sm font-mono">
                            {t}
                          </span>
                        ))}
                        {((s.brand_names || []).length + (s.category_names || []).length) === 0 && (
                          <span className="text-slate-600 text-xs">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {s.rating > 0 ? (
                        <div className="flex items-center gap-1 text-yellow-400 text-xs font-mono">
                          <Star className="w-3 h-3 fill-current" /> {s.rating.toFixed(1)}
                        </div>
                      ) : (
                        <span className="text-slate-600 text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={`delete-supplier-${s.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={(e) => remove(e, s.id)}
                        className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 tracking-tight">Yeni Tedarikçi</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Firma Adı *</Label>
                <Input
                  data-testid="supplier-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Vergi No</Label>
                <VknLookupField
                  value={form.tax_no}
                  onChange={(v) => setForm({ ...form, tax_no: v })}
                  onCompanyFound={(c) => setForm((p) => ({ ...p, name: p.name || c.title }))}
                  testId="supplier-tax-no"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Vergi Dairesi</Label>
                <Input
                  data-testid="supplier-tax-office"
                  value={form.tax_office || ""}
                  onChange={(e) => setForm({ ...form, tax_office: e.target.value })}
                  placeholder="örn: KADIKÖY"
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Yetkili</Label>
                <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">E-posta</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">İl</Label>
                <Select
                  value={form.city || "__none__"}
                  onValueChange={(v) => setForm({ ...form, city: v === "__none__" ? "" : v, district: "" })}
                >
                  <SelectTrigger data-testid="supplier-city" className="bg-slate-950 border-slate-700 rounded-sm focus:ring-blue-500">
                    <SelectValue placeholder="İl seçin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">— İl Seçilmedi —</SelectItem>
                    {PROVINCES.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
                  İlçe
                  {!form.city && (
                    <span className="ml-1 text-[10px] normal-case font-sans text-slate-600">
                      · önce il seçin
                    </span>
                  )}
                </Label>
                <Select
                  disabled={!form.city}
                  value={form.district || "__none__"}
                  onValueChange={(v) => setForm({ ...form, district: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger data-testid="supplier-district" className="bg-slate-950 border-slate-700 rounded-sm focus:ring-blue-500 disabled:opacity-40">
                    <SelectValue placeholder={form.city ? "İlçe seçin" : "—"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">— İlçe Seçilmedi —</SelectItem>
                    {districtsOf(form.city).map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Adres</Label>
                <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 resize-none" placeholder="Cadde, sokak, no, posta kodu…" />
              </div>
              <div className="col-span-2 pt-2 mt-1 border-t border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
                  Bu tedarikçiden temin edilen malzemeler
                </div>
              </div>
              <div className="space-y-2 col-span-2">
                <SupplierMaterialPicker
                  kind="fabric"
                  value={form.fabric_ids}
                  onChange={(ids) => setForm({ ...form, fabric_ids: ids })}
                  testIdPrefix="supplier-fabric-picker"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <SupplierMaterialPicker
                  kind="accessory"
                  value={form.accessory_ids}
                  onChange={(ids) => setForm({ ...form, accessory_ids: ids })}
                  testIdPrefix="supplier-accessory-picker"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <DefinitionPicker
                  kind="brand"
                  sourceUrl="/suppliers/brand-options"
                  label="Ürün Markaları"
                  value={form.brand_names}
                  onChange={(names) => setForm({ ...form, brand_names: names })}
                  testIdPrefix="supplier-brand-picker"
                  placeholder="Marka ara…"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <DefinitionPicker
                  kind="product_category"
                  label="Ürün Kategorileri"
                  value={form.category_names}
                  onChange={(names) => setForm({ ...form, category_names: names })}
                  testIdPrefix="supplier-category-picker"
                  placeholder="Kategori ara…"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Notlar</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 resize-none" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
                İptal
              </Button>
              <Button
                data-testid="save-supplier-btn"
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ExcelPartyImportDialog
        kind="suppliers"
        open={excelImportOpen}
        onClose={() => setExcelImportOpen(false)}
        onImported={load}
      />
    </div>
  );
}
